import React, { FC, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCurrentlyLoggedInUser } from '../../CommonFunctions';
import config from '../../config';
import DashboardHeader from './SharedDashboardViews/DashboardHeader';
import DailySalesChart from './SharedDashboardViews/DailySalesChart';
import ActivitySummary from './SharedDashboardViews/ActivitySummary';
import ParticipantSalesTable from './SharedDashboardViews/ParticipantSalesTable';
import FinancialBreakdown from './SharedDashboardViews/FinancialBreakdown';
import { TeamAdminDashboardData, TeamAdminDivision } from './SharedDashboardViews/types';

type RouteParams = Record<string, string | undefined>;

const BRAND = '#163172';
const GOLD  = '#ffc20e';

const fetchWithAuth = async (url: string) => {
  const token = localStorage.getItem('accessToken');
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.json();
};

// Skeleton matches real layout: tabs → hero → 4-col summary → table
const SkeletonCard: FC<{ h?: number }> = ({ h = 120 }) => (
  <div className="rounded-3" style={{ height: h, background: 'linear-gradient(90deg,#e8eaf6 25%,#dde2f5 50%,#e8eaf6 75%)', animation: 'pulse 1.5s infinite' }} />
);

const TeamAdminSkeleton: FC = () => (
  <div className="p-2 p-md-3" style={{ background: '#f8f9fb', minHeight: '100vh' }}>
    <div className="mb-3" style={{ height: 40, background: 'linear-gradient(90deg,#e8eaf6 25%,#dde2f5 50%,#e8eaf6 75%)', borderRadius: 8 }} />
    <SkeletonCard h={140} />
    <div className="row g-3 mt-2 mb-3">
      <div className="col-6 col-md-3"><SkeletonCard h={160} /></div>
      <div className="col-6 col-md-3"><SkeletonCard h={160} /></div>
      <div className="col-6 col-md-3"><SkeletonCard h={160} /></div>
      <div className="col-6 col-md-3"><SkeletonCard h={160} /></div>
    </div>
    <SkeletonCard h={260} />
  </div>
);

const TeamAdminDashboard: FC = () => {
  const { linkExtension } = useParams<RouteParams>();
  const fundraiserId = linkExtension ? parseInt(linkExtension, 10) : 0;
  const navigate = useNavigate();

  const [accountId, setAccountId] = useState<number | null>(null);
  const [divisions, setDivisions] = useState<TeamAdminDivision[]>([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState<number | null>(null);
  const [dashboardData, setDashboardData] = useState<TeamAdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    document.title = 'Team Admin Dashboard';
    getCurrentlyLoggedInUser()
      .then(user => setAccountId(user.id))
      .catch(() => navigate('/login'));
  }, [navigate]);

  useEffect(() => {
    if (!accountId || !fundraiserId) return;
    fetchWithAuth(
      `${config.API_BASE_URL}dashboards/teamAdminDivisions/?participant_account_id=${accountId}&fundraiser_id=${fundraiserId}`
    )
      .then((data: TeamAdminDivision[]) => {
        setDivisions(data);
        if (data.length > 0) setSelectedDivisionId(data[0].secondary_id);
      })
      .catch(err => setError(err.message));
  }, [accountId, fundraiserId]);

  const fetchDashboard = useCallback(async () => {
    if (!accountId || !fundraiserId || !selectedDivisionId) return;
    try {
      setLoading(true);
      const data: TeamAdminDashboardData = await fetchWithAuth(
        `${config.API_BASE_URL}dashboards/teamAdmin/?team_admin_account_id=${accountId}&fundraiser_id=${fundraiserId}&secondary_division_id=${selectedDivisionId}`
      );
      setDashboardData(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [accountId, fundraiserId, selectedDivisionId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleDivisionChange = (id: number) => {
    setSelectedDivisionId(id);
    setDashboardData(null);
  };

  const currentDivision = divisions.find(d => d.secondary_id === selectedDivisionId);
  const multipleTeams   = divisions.length > 1;

  if (loading && !dashboardData) return <TeamAdminSkeleton />;

  if (error) {
    return (
      <div className="d-flex align-items-center justify-content-center p-5" style={{ minHeight: '60vh', background: '#f8f9fb' }}>
        <div className="bg-white rounded-3 shadow-sm p-5 text-center" style={{ maxWidth: 420 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <h5 className="fw-bold mb-2">Failed to load dashboard</h5>
          <p className="text-muted small mb-4">{error}</p>
          <button onClick={fetchDashboard} className="btn btn-sm fw-semibold text-white px-4" style={{ background: BRAND, border: 'none' }}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (divisions.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center p-5" style={{ minHeight: '60vh', background: '#f8f9fb' }}>
        <div className="bg-white rounded-3 shadow-sm p-5 text-center" style={{ maxWidth: 420 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏫</div>
          <h5 className="fw-bold mb-2">No team assigned</h5>
          <p className="text-muted small">You don't have a team assigned for this fundraiser. Contact your coordinator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 p-md-3" style={{ background: '#f8f9fb', minHeight: '100vh' }}>

      {/* Division tabs — only when managing multiple teams */}
      {multipleTeams && (
        <div className="mb-3">
          <p className="text-muted mb-2" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Your teams
          </p>

          {/* Desktop: horizontal tab bar with gold underline on active */}
          <div
            className="d-none d-md-flex rounded-3"
            style={{ border: `1.5px solid ${BRAND}30`, background: '#fff', overflow: 'hidden' }}
          >
            {divisions.map(div => {
              const active = div.secondary_id === selectedDivisionId;
              return (
                <button
                  key={div.secondary_id}
                  onClick={() => handleDivisionChange(div.secondary_id)}
                  className="flex-grow-1 py-2 px-3 border-0"
                  style={{
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    background: active ? BRAND : 'transparent',
                    color: active ? '#fff' : '#475569',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    borderRight: `1px solid ${BRAND}20`,
                    outline: 'none',
                    boxShadow: active ? `inset 0 -3px 0 ${GOLD}` : 'none',
                  }}
                  aria-pressed={active}
                >
                  {div.secondary_division}
                  {div.primary_division && (
                    <span className="ms-1 d-block d-lg-inline" style={{ fontSize: 11, opacity: 0.75, fontWeight: 400 }}>
                      · {div.primary_division}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Mobile: stacked select-style buttons with bold active text */}
          <div className="d-flex d-md-none flex-column gap-2">
            {divisions.map(div => {
              const active = div.secondary_id === selectedDivisionId;
              return (
                <button
                  key={div.secondary_id}
                  onClick={() => handleDivisionChange(div.secondary_id)}
                  className="w-100 text-start rounded-3 py-2 px-3 border-0 d-flex align-items-center justify-content-between"
                  style={{
                    fontSize: 14,
                    fontWeight: active ? 700 : 500,
                    background: active ? BRAND : '#fff',
                    color: active ? '#fff' : '#475569',
                    border: `1.5px solid ${active ? BRAND : '#e2e8f0'}`,
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                  aria-pressed={active}
                >
                  <span>{div.secondary_division}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {active && (
                      <span style={{ fontSize: 10, background: GOLD, color: '#7a5500', borderRadius: 10, padding: '2px 7px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        active
                      </span>
                    )}
                    {div.primary_division && (
                      <span style={{ fontSize: 11, opacity: 0.75, fontWeight: 400 }}>{div.primary_division}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {dashboardData && (
        <>
          {/* Hero banner */}
          <DashboardHeader
            fundraiserName={dashboardData.fundraiser_name}
            fundraiserCode={currentDivision?.secondary_division ?? ''}
            scope={currentDivision?.primary_division ?? 'Team'}
            stats={{
              total_raised: dashboardData.total_sales_amount,
              participant_count: dashboardData.participant_sales_table.length,
              total_orders: dashboardData.participant_sales_table.reduce((s, r) => s + r.num_items_sold, 0),
              days_remaining: dashboardData.days_remaining,
            }}
            role="Team Admin"
            lastUpdated={lastUpdated}
            onRefresh={fetchDashboard}
          />

          {/* Summary row: Revenue · Activity · Financials · Team Metrics — all above the table */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-sm-6 col-md-3">
              <DailySalesChart data={dashboardData.last_seven_daily_sales} />
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <ActivitySummary data={dashboardData.participant_active_summary} />
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <FinancialBreakdown
                data={{ total_raised: dashboardData.total_sales_amount, total_profit: dashboardData.total_profit }}
              />
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <div className="bg-white rounded-3 shadow-sm p-3 h-100" style={{ border: '1px solid #f0f0f0' }}>
                <h6 className="fw-bold mb-3" style={{ color: '#1a1a2e', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Team Metrics
                </h6>
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: 12, color: '#475569' }}>Total participants</span>
                    <span className="fw-bold" style={{ fontSize: 14, color: BRAND }}>
                      {dashboardData.participant_sales_table.length}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                    <span style={{ fontSize: 12, color: '#475569' }}>Items sold</span>
                    <span className="fw-bold" style={{ fontSize: 14, color: '#475569' }}>
                      {dashboardData.participant_sales_table.reduce((s, r) => s + r.num_items_sold, 0)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                    <span style={{ fontSize: 12, color: '#475569' }}>Active this week</span>
                    <span className="fw-bold" style={{ fontSize: 14, color: '#059669' }}>
                      {dashboardData.participant_active_summary.active + dashboardData.participant_active_summary.yesterday + dashboardData.participant_active_summary.last_week}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                    <span style={{ fontSize: 12, color: '#475569' }}>Avg per participant</span>
                    <span className="fw-bold" style={{ fontSize: 14, color: BRAND }}>
                      {dashboardData.participant_sales_table.length > 0
                        ? `$${(dashboardData.total_sales_amount / dashboardData.participant_sales_table.length).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                        : '$0'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Participant table */}
          <div className="bg-white rounded-3 shadow-sm p-3" style={{ border: '1px solid #f0f0f0' }}>
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <h6 className="fw-bold mb-0" style={{ color: '#1a1a2e', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Team Participants
              </h6>
              <span className="badge rounded-pill fw-normal" style={{ background: '#f0f3ff', color: BRAND, fontSize: 11 }}>
                {dashboardData.participant_sales_table.length} total
              </span>
            </div>
            <ParticipantSalesTable
              rows={dashboardData.participant_sales_table}
              showDivision={false}
              groupByTeamAdmin={false}
              fundraiserId={fundraiserId}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default TeamAdminDashboard;
